<template>
    <div v-if="showNetworkSpecificReminder || showGenericReminder" class="box">
        <g-base-box background-color="grey-2" class="network-connect-reminder">
            <g-icon v-if="location === 'dashboard'" name="chart-bar" data-testid="network-icon" />

            <network-icon v-else :network="NETWORK_INFO_BY_ID[networkId].slug" data-testid="network-icon" />

            <div class="network-connect-reminder-content">
                <g-typography v-if="location === 'dashboard'" font-weight="semibold" tag="p">
                    Connect your social networks to automatically sync and share your content metrics with brands.
                </g-typography>
                <g-typography v-else font-weight="semibold" tag="p">
                    Save time by connecting your {{ NETWORK_INFO_BY_ID[networkId].name }} account. This automatically
                    syncs your content and content metrics to brands.
                </g-typography>

                <g-typography font-weight="light" class="subtitle" tag="p">
                    Your security is important to us. We’ll never see your password or share your personal information.
                </g-typography>

                <div class="buttons">
                    <g-button
                        v-if="location !== 'dashboard'"
                        theme="live"
                        shape="round"
                        data-testid="learn-more"
                        @click="learnMore"
                    >
                        Learn More
                    </g-button>
                    <g-button theme="live" type="primary" shape="round" data-testid="connect" @click="connect">
                        {{ location === 'dashboard' ? 'Connect Networks' : 'Connect' }}
                    </g-button>
                </div>
            </div>
        </g-base-box>
    </div>
</template>

<script>
    import { computed, onBeforeMount, toRefs, ref } from 'vue-demi';
    import {
        EVENT_NAME_CONNECT_REMINDER_CONNECT_CLICKED,
        EVENT_NAME_CONNECT_REMINDER_LEARN_MORE_CLICKED
    } from '../../../../app-v2/constants/analytics-events';
    import { FAQ_CONNECTING_NETWORKS_PERMISSIONS } from '../../../../app-v2/constants/faqs';
    import { NETWORK_INFO_BY_ID, NETWORK_CONNECTION_TYPE_OAUTH } from '../../../../app-v2/constants/networks';
    import ConnectNetworkItem from '../../../../app-v2/components/data-views/networks/partials/ConnectNetworkItem.vue';
    import NetworkIcon from '../../interface/elements/NetworkIcon.vue';
    import NetworkConnectionMixin from '../../../../app-v2/mixins/NetworkConnectionMixin';
    import EventTrackingMixin from '../../../../app-v2/mixins/EventTrackingMixin';
    import { useContactStore } from '../../../../stores';
    import useRouter from '../../../composables/use-router';
    import useEventTracking from '../../../composables/use-event-tracking';
    import useHttp from '../../../composables/use-http';
    import useNetworks from '../../../composables/use-networks';

    export default {
        name: 'NetworkConnectReminder',
        mixins: [NetworkConnectionMixin, EventTrackingMixin],
        components: { NetworkIcon },
        props: {
            location: {
                type: String,
                default: ''
            },
            networkId: {
                type: Number,
                default: 5
            }
        },
        setup(props, context) {
            const router = useRouter().get();
            const contact = useContactStore().data;
            const events = useEventTracking();
            const { networkId, location } = toRefs(props);
            const showGenericReminder = ref(false);

            const learnMore = () => {
                events.trackEvent(EVENT_NAME_CONNECT_REMINDER_LEARN_MORE_CLICKED, {
                    location: location.value,
                    network: NETWORK_INFO_BY_ID[networkId.value].slug
                });
                window.open(FAQ_CONNECTING_NETWORKS_PERMISSIONS);
            };

            const connect = () => {
                events.trackEvent(EVENT_NAME_CONNECT_REMINDER_CONNECT_CLICKED, {
                    location: location.value,
                    network: networkId.value ? NETWORK_INFO_BY_ID[networkId.value].slug : null
                });
                if (location.value === 'dashboard') {
                    router.push({
                        name: 'manage.networks'
                    });
                } else if (props.location === 'deliver_activation') {
                    router.push({
                        name: 'manage.networks',
                        query: {
                            connectById: networkId.value,
                            taskId: router.history?.current.params.task_id,
                            deliverableId: router.history?.current.params.deliverable_id,
                            briefId: router.history?.current.params.brief_id
                        }
                    });
                } else {
                    router.push({
                        name: 'manage.networks',
                        query: { connectById: networkId.value }
                    });
                }
            };

            const getShowGenericReminder = async () => {
                if (location.value === 'dashboard') {
                    const { http } = useHttp();
                    await http
                        .get(`/api/v1/contacts/${context.root.contact_id}/networks/notAuthedParticipatingNetworks`)
                        .then((res) => {
                            showGenericReminder.value = res.data?.length > 0;
                        });
                }
            };

            const showNetworkSpecificReminder = computed(() => {
                if (networkId.value && location.value !== 'dashboard') {
                    return !useNetworks().isConnectedByIdFromContactNetworks(networkId.value, context.root.networks);
                } else {
                    return false;
                }
            });

            onBeforeMount(async () => {
                await getShowGenericReminder();
            });

            return {
                NETWORK_INFO_BY_ID,
                learnMore,
                connect,
                ConnectNetworkItem,
                contact,
                showNetworkSpecificReminder,
                showGenericReminder,
                getShowGenericReminder
            };
        }
    };
</script>

<style lang="scss" scoped>
    .network-connect-reminder {
        display: flex;

        &-icon {
            width: 5%;
        }

        &-content {
            margin-left: $g-spacing-4;
        }

        p {
            margin-bottom: $g-spacing-3;
        }

        .buttons {
            margin-top: $g-spacing-5;
            margin-bottom: 0;

            button {
                margin-right: $g-spacing-3;
            }
        }

        .subtitle {
            font-size: $g-spacing-4;
        }
    }
</style>
